import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, Copy, Check, Trash2, ExternalLink } from 'lucide-react';
import './RyanFlashcardPreviewModal.css';

interface SubdeckData {
  _id: string;
  name: string;
  deckId: string;
  type: 'flashcards' | 'apostila';
}

interface CardItem {
  _id: string;
  front: string;
  back: string;
}

interface ProductOption {
  _id: string;
  title: string;
  publicSlug: string | null;
}

interface ActivePreview {
  _id: string;
  urlSlug: string;
  duration: string;
  expiresAt: string | null;
  cardCount: number;
  maxCardsPerSession: number | null;
  createdAt: string;
}

type PreviewDuration = '15min' | '30min' | '60min' | '3h' | '6h' | '12h' | '24h' | 'always';

const DURATION_LABELS: Record<PreviewDuration, string> = {
  '15min': '15min',
  '30min': '30min',
  '60min': '1h',
  '3h': '3h',
  '6h': '6h',
  '12h': '12h',
  '24h': '24h',
  'always': 'Sempre',
};

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function formatTimeLeft(expiresAt: string | null): string {
  if (!expiresAt) return 'Sempre disponível';
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return 'Expirado';
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  return diffH > 0 ? `${diffH}h ${diffMin % 60}min restantes` : `${diffMin}min restantes`;
}

const RyanFlashcardPreviewModal: React.FC<{
  spaceId: string;
  subdeck: SubdeckData;
  onClose: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ spaceId, subdeck, onClose, onToast }) => {
  const [cards, setCards] = useState<CardItem[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());

  const [duration, setDuration] = useState<PreviewDuration>('always');
  const [productId, setProductId] = useState('');
  const [maxCards, setMaxCards] = useState('');
  const [products, setProducts] = useState<ProductOption[]>([]);

  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [activePreviews, setActivePreviews] = useState<ActivePreview[]>([]);
  const [previewsLoading, setPreviewsLoading] = useState(true);

  const token = localStorage.getItem('accessToken');

  const loadCards = useCallback(async () => {
    setCardsLoading(true);
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/cards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCards(data.cards || []);
      }
    } finally {
      setCardsLoading(false);
    }
  }, [spaceId, subdeck._id, token]);

  const loadPreviews = useCallback(async () => {
    setPreviewsLoading(true);
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/previews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setActivePreviews(await res.json());
    } finally {
      setPreviewsLoading(false);
    }
  }, [spaceId, subdeck._id, token]);

  const loadProducts = useCallback(async () => {
    try {
      const res = await fetch('/api/ryan/admin/products', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProducts((data.products || data || []).map((p: any) => ({
          _id: p._id,
          title: p.title,
          publicSlug: p.publicSlug || null,
        })));
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    loadCards();
    loadPreviews();
    loadProducts();
  }, [loadCards, loadPreviews, loadProducts]);

  const toggleCard = (id: string) => {
    setSelectedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedCardIds(new Set(cards.map(c => c._id)));
  const clearAll = () => setSelectedCardIds(new Set());

  const handleGenerate = async () => {
    if (selectedCardIds.size === 0) {
      onToast('Selecione ao menos um card.', 'error');
      return;
    }
    setGenerating(true);
    try {
      const body: any = {
        cardIds: [...selectedCardIds],
        duration,
      };
      if (productId) body.productId = productId;
      if (maxCards && parseInt(maxCards) > 0) body.maxCardsPerSession = parseInt(maxCards);

      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro');

      const url = `${window.location.origin}/ryan/previa-cards/${data.preview.urlSlug}`;
      setGeneratedUrl(url);
      loadPreviews();
    } catch (err: any) {
      onToast(err.message || 'Erro ao gerar prévia', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (previewId: string) => {
    try {
      const res = await fetch(`/api/ryan/admin/previews/${previewId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setActivePreviews(prev => prev.filter(p => p._id !== previewId));
        onToast('Prévia revogada.', 'success');
      }
    } catch {
      onToast('Erro ao revogar prévia', 'error');
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rfpm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rfpm-modal">
        <div className="rfpm-header">
          <div>
            <h3 className="rfpm-title">Configurar prévia</h3>
            <p className="rfpm-subtitle">{subdeck.name}</p>
          </div>
          <button className="rfpm-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="rfpm-body">
          {/* Seção A: Seleção de cards */}
          <div className="rfpm-section">
            <div className="rfpm-section-header">
              <span className="rfpm-section-label">Cards para a prévia</span>
              <span className="rfpm-card-count">
                {selectedCardIds.size} de {cards.length} selecionados
              </span>
            </div>
            <div className="rfpm-card-actions">
              <button className="rfpm-link-btn" onClick={selectAll}>Selecionar todos</button>
              <span className="rfpm-link-sep">·</span>
              <button className="rfpm-link-btn" onClick={clearAll}>Limpar seleção</button>
            </div>
            {cardsLoading ? (
              <div className="rfpm-loading">Carregando cards...</div>
            ) : cards.length === 0 ? (
              <div className="rfpm-empty">Nenhum card encontrado neste deck.</div>
            ) : (
              <div className="rfpm-card-list">
                {cards.map(card => (
                  <label key={card._id} className={`rfpm-card-item${selectedCardIds.has(card._id) ? ' selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedCardIds.has(card._id)}
                      onChange={() => toggleCard(card._id)}
                    />
                    <div className="rfpm-card-preview-both">
                      <span className="rfpm-card-q">{stripHtml(card.front).slice(0, 100)}</span>
                      <span className="rfpm-card-a">{stripHtml(card.back).slice(0, 100)}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Seção B: Duração */}
          <div className="rfpm-section">
            <div className="rfpm-section-label">
              <Clock size={13} style={{ display: 'inline', marginRight: 5 }} />
              Duração do link
            </div>
            <div className="rfpm-duration-row">
              {(Object.keys(DURATION_LABELS) as PreviewDuration[]).map(d => (
                <button
                  key={d}
                  className={`rfpm-duration-btn${duration === d ? ' active' : ''}`}
                  onClick={() => setDuration(d)}
                >
                  {DURATION_LABELS[d]}
                </button>
              ))}
            </div>
          </div>

          {/* Seção C: Produto vinculado */}
          <div className="rfpm-section">
            <div className="rfpm-section-label">Produto vinculado <span className="rfpm-optional">(opcional)</span></div>
            <select
              className="rfpm-select"
              value={productId}
              onChange={e => setProductId(e.target.value)}
            >
              <option value="">— Nenhum —</option>
              {products.map(p => (
                <option key={p._id} value={p._id}>{p.title}</option>
              ))}
            </select>
            <p className="rfpm-hint">Quando a prévia expirar, o usuário será redirecionado para a página deste produto.</p>
          </div>

          {/* Seção D: Limite por sessão */}
          <div className="rfpm-section">
            <div className="rfpm-section-label">Limite de cards por sessão <span className="rfpm-optional">(opcional)</span></div>
            <input
              type="number"
              min={1}
              placeholder="Sem limite"
              className="rfpm-input"
              value={maxCards}
              onChange={e => setMaxCards(e.target.value)}
            />
            <p className="rfpm-hint">Número máximo de cards que o usuário pode estudar por sessão antes de ver a tela de encerramento.</p>
          </div>

          {/* Botão gerar */}
          {!generatedUrl ? (
            <button
              className="rfpm-generate-btn"
              onClick={handleGenerate}
              disabled={generating || selectedCardIds.size === 0}
            >
              {generating ? 'Gerando...' : `Gerar link de prévia (${selectedCardIds.size} cards)`}
            </button>
          ) : (
            <div className="rfpm-generated">
              <p className="rfpm-generated-msg">
                Link gerado! Duração: <strong>{DURATION_LABELS[duration]}</strong> · {selectedCardIds.size} cards
              </p>
              <div className="rfpm-url-row">
                <input className="rfpm-url-input" readOnly value={generatedUrl} onClick={e => (e.target as HTMLInputElement).select()} />
                <button className={`rfpm-copy-btn${copied ? ' copied' : ''}`} onClick={() => handleCopy(generatedUrl!)}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <div className="rfpm-generated-actions">
                <button className="rfpm-link-btn" onClick={() => { setGeneratedUrl(null); setSelectedCardIds(new Set()); }}>
                  Gerar novo link
                </button>
                <a className="rfpm-link-btn rfpm-open-link" href={generatedUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={12} /> Abrir prévia
                </a>
              </div>
            </div>
          )}

          {/* Seção E: Prévias ativas */}
          {(previewsLoading || activePreviews.length > 0) && (
            <div className="rfpm-section rfpm-section--active">
              <div className="rfpm-section-label">Links ativos</div>
              {previewsLoading ? (
                <div className="rfpm-loading">Carregando...</div>
              ) : (
                <div className="rfpm-active-list">
                  {activePreviews.map(p => (
                    <div key={p._id} className="rfpm-active-item">
                      <div className="rfpm-active-info">
                        <span className="rfpm-active-slug">/{p.urlSlug}</span>
                        <span className="rfpm-active-meta">
                          <Clock size={10} />
                          {formatTimeLeft(p.expiresAt)}
                          {' · '}{p.cardCount} cards
                          {p.maxCardsPerSession && ` · máx ${p.maxCardsPerSession}/sessão`}
                        </span>
                      </div>
                      <div className="rfpm-active-btns">
                        <button
                          className="rfpm-active-copy"
                          title="Copiar link"
                          onClick={() => handleCopy(`${window.location.origin}/ryan/previa-cards/${p.urlSlug}`)}
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          className="rfpm-active-revoke"
                          title="Revogar"
                          onClick={() => handleRevoke(p._id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RyanFlashcardPreviewModal;
